import AntIcon from '@expo/vector-icons/AntDesign';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KeyboardTypeOptions,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputProps,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { TextInputMask } from 'react-native-masked-text';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { AppColors } from 'src/themes/Colors';
import { fontSizes, iconSize, metrics, pixelScaler } from 'src/themes/Metrics';
import styled from 'styled-components/native';
import { Container } from './Container';

import { RegularText } from './RegularText';
import { Row } from './Row';
import { SizedBox } from './SizedBox';

const Styled = {
  OuterWrapper: styled.View``,
  Wrapper: styled.View`
    flex-direction: row;
    height: ${metrics.inputHeight}px;
    align-self: stretch;
    border-color: ${AppColors.border};
    border-bottom-width: ${StyleSheet.hairlineWidth}px;
  `,
  // padding: ${metrics.medium}px;
  // background-color: yellow;
  // border-radius: ${metrics.borderRadius}px;
};

interface Props {
  labelLang?: string;
  value: any;
  onChangeText: (masked: any) => void;
  autoFocus?: boolean;
  onPressIn?(): void;
  onPressOut?(): void;
  // onFocus?(): void;
  // onBlur?(): void;
  keyboardType?: KeyboardTypeOptions;
  showClearButton?: boolean;
  size?: number;
  wrapperStyle?: StyleProp<ViewStyle>;
  labelWrapperStyle?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
  inputRef?: (ref: any) => void;
  inputProps?: TextInputProps;
  editable?: boolean;
  maxLenght?: number;
  hideCursor?: boolean;
  displayKeyboard?: boolean;
  isRequire?: boolean;
  mask?: string;
  error?: string;
  row?: boolean;
}
export const PrimaryInput = ({
  value = '',
  showClearButton: canClear = true,
  labelWrapperStyle = null,
  inputStyle = null,
  autoFocus = false,
  onChangeText = () => {},
  onPressIn = () => {},
  onPressOut = () => {},
  inputRef = () => {},
  keyboardType,
  inputProps = {},
  editable,
  maxLenght,
  hideCursor = false,
  displayKeyboard = true,
  labelLang,
  isRequire,
  mask,
  error,
  row,
}: Props) => {
  const labelValue = useSharedValue(26);
  const labelSize = useSharedValue(pixelScaler(16));
  const { t } = useTranslation();

  const runningStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateY: labelValue.value,
      },
    ],
  }));
  const labelStyle = useAnimatedStyle(() => ({
    fontSize: labelSize.value,
  }));

  const handleFocus = () => {
    labelValue.value = withTiming(8, {
      duration: 200,
      easing: Easing.linear,
    });
    labelSize.value = withTiming(pixelScaler(12), {
      duration: 200,
      easing: Easing.linear,
    });
  };

  const handleBlur = () => {
    if (value === '') {
      labelValue.value = withTiming(26, {
        duration: 200,
        easing: Easing.linear,
      });
      labelSize.value = withTiming(pixelScaler(16), {
        duration: 30,
        easing: Easing.linear,
      });
    } else {
      labelValue.value = withTiming(8, {
        duration: 200,
        easing: Easing.linear,
      });
      labelSize.value = withTiming(pixelScaler(12), {
        duration: 30,
        easing: Easing.linear,
      });
    }
  };

  useEffect(() => {
    handleBlur();
  }, [value]);

  return (
    <Styled.OuterWrapper style={row ? { flex: 1 } : { width: '100%' }}>
      <Styled.Wrapper>
        <Container flex>
          <Animated.View
            style={{
              position: 'absolute',
              left: 0,
              paddingBottom: metrics.small,
              ...runningStyle,
            }}
            pointerEvents="none">
            <Row>
              <Animated.Text style={[labelWrapperStyle, labelStyle, { color: AppColors.gray }]}>
                {t(labelLang || '')}
              </Animated.Text>

              <SizedBox width="tiny" />
              {isRequire && (
                <RegularText color={AppColors.red} fontSize="medium">
                  *
                </RegularText>
              )}
            </Row>
          </Animated.View>
          <Container flex>
            {mask ? (
              <TextInputMask
                options={{
                  mask,
                }}
                type="custom"
                caretHidden={hideCursor}
                showSoftInputOnFocus={displayKeyboard}
                ref={ref => inputRef(ref)}
                keyboardType={keyboardType}
                value={value}
                autoFocus={autoFocus}
                editable={editable}
                maxLength={maxLenght}
                onChangeText={onChangeText}
                onPressIn={onPressIn}
                onPressOut={onPressOut}
                onFocus={handleFocus}
                onBlur={handleBlur}
                selectionColor="transparent"
                {...inputProps}
                style={[
                  styles.inputStyle,
                  inputStyle,
                  {
                    outlineStyle: 'none',
                    height: '100%',
                  } as any,
                ]}
              />
            ) : (
              <TextInput
                caretHidden={hideCursor}
                showSoftInputOnFocus={displayKeyboard}
                ref={ref => inputRef(ref)}
                keyboardType={keyboardType}
                value={value}
                autoFocus={autoFocus}
                editable={editable}
                maxLength={maxLenght}
                onChangeText={onChangeText}
                onPressIn={onPressIn}
                onPressOut={onPressOut}
                onFocus={handleFocus}
                onBlur={handleBlur}
                selectionColor="transparent"
                {...inputProps}
                style={[
                  styles.inputStyle,
                  inputStyle,
                  {
                    outlineStyle: 'none',
                    height: '100%',
                  } as any,
                ]}
              />
            )}
          </Container>
        </Container>
        {canClear && !!value ? (
          <TouchableOpacity
            onPress={() => {
              onChangeText('');
              labelValue.value = withTiming(10, {
                duration: 200,
                easing: Easing.linear,
              });
            }}
            style={styles.iconWrapper}
            hitSlop={{ top: 10, right: 10, bottom: 5, left: 10 }}>
            <AntIcon name="close" size={iconSize.medium} color={AppColors.black} />
          </TouchableOpacity>
        ) : (
          <View />
        )}
      </Styled.Wrapper>
      {error && (
        <RegularText style={{ marginTop: metrics.small }} textAlign="right" color={AppColors.red} fontSize="small">
          {error}
        </RegularText>
      )}
    </Styled.OuterWrapper>
  );
};

const styles = StyleSheet.create({
  iconWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: metrics.large,
    width: 30,
  },
  inputStyle: {
    fontSize: fontSizes.regular,
    flex: 1,
  },
});
